import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";

export interface Building {
  id: string;
  name: string;
  address: string;
  role: string;
}

/** Sentinel value representing "all buildings" selection — never triggers redirect */
export const ALL_BUILDING: Building = {
  id: "__ALL__",
  name: "All Buildings",
  address: "",
  role: "admin",
};

/** Sentinel value representing company-level (non-building) operations */
export const COMPANY_BUILDING: Building = {
  id: "COMPANY",
  name: "Company",
  address: "",
  role: "admin",
};

interface BuildingContextType {
  currentBuilding: Building | null;
  setCurrentBuilding: (building: Building | null) => void;
  isLoading: boolean;
}

const BuildingContext = createContext<BuildingContextType | undefined>(
  undefined,
);

// Singleton pattern to allow setting building from non-React code
let globalSetBuilding: ((building: Building | null) => void) | null = null;

export const setBuildingContext = (building: Building | null) => {
  if (globalSetBuilding) {
    globalSetBuilding(building);
  }
};

interface BuildingProviderProps {
  children: ReactNode;
}

export const BuildingProvider: React.FC<BuildingProviderProps> = ({
  children,
}) => {
  const [currentBuilding, setCurrentBuilding] = useState<Building | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Set the global setter function
  globalSetBuilding = setCurrentBuilding;

  useEffect(() => {
    // Load building from local storage on mount
    const loadStoredBuilding = () => {
      setIsLoading(true);
      try {
        const storedBuilding = localStorage.getItem("currentBuilding");
        if (storedBuilding) {
          setCurrentBuilding(JSON.parse(storedBuilding));
        }
      } catch (error) {
        console.error("Error loading building from storage:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredBuilding();
  }, []);

  // Handle building selection
  const handleSetBuilding = (building: Building | null) => {
    setCurrentBuilding(building);

    if (building) {
      localStorage.setItem("currentBuilding", JSON.stringify(building));
    } else {
      localStorage.removeItem("currentBuilding");
      // Redirect to building selection if building is nullified
      navigate("/select-building");
    }
  };

  return (
    <BuildingContext.Provider
      value={{
        currentBuilding,
        setCurrentBuilding: handleSetBuilding,
        isLoading,
      }}
    >
      {children}
    </BuildingContext.Provider>
  );
};

export const useBuilding = (): BuildingContextType => {
  const context = useContext(BuildingContext);
  if (context === undefined) {
    throw new Error("useBuilding must be used within a BuildingProvider");
  }
  return context;
};
